// Runtime Mapbox token for the SPA MapBlock. Fetched from the Worker
// (/api/mapbox-token), which brokers a short-lived minted token from eve, so no
// long-lived token is baked into the bundle (a revoked/rotated token can't
// silently blank the maps until a rebuild). Cached in module state and refetched
// within a margin of expiry. Falls back to the build-time VITE_MAPBOX_TOKEN when
// the runtime fetch is unavailable — mainly the dev gallery (no /api backend) and
// local dev.
const STATIC_FALLBACK = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) || '';
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
let cached: { token: string; expiresAt: number } | null = null;
let inflight: Promise<string | null> | null = null;

export async function getMapboxToken(): Promise<string | null> {
  const now = Date.now();
  if (cached && cached.expiresAt - now > REFRESH_MARGIN_MS) return cached.token;
  inflight ??= fetchToken().finally(() => {
    inflight = null;
  });
  const fresh = await inflight;
  if (fresh) return fresh;
  if (cached && cached.expiresAt - Date.now() > 0) return cached.token; // stale-but-valid
  return STATIC_FALLBACK || null; // dev/gallery or runtime failure
}

async function fetchToken(): Promise<string | null> {
  try {
    const resp = await fetch('/api/mapbox-token', { credentials: 'same-origin' });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { token?: unknown; expiresAt?: unknown };
    if (typeof body.token !== 'string' || !body.token || typeof body.expiresAt !== 'number') return null;
    cached = { token: body.token, expiresAt: body.expiresAt };
    return body.token;
  } catch {
    return null;
  }
}
